import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, setDoc } from 'firebase/firestore';
import { storage, db } from '../firebase';
import VaultLogo from '../assets/Vault.svg';

/**
 * Load and convert SVG logo to PNG base64 for PDF
 */
const loadLogoBase64 = async () => {
  try {
    const response = await fetch(VaultLogo);
    const svgText = await response.text();
    
    const img = new Image();
    const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    
    return new Promise((resolve, reject) => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 300;
        canvas.height = 300;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, 300, 300);
        
        const pngBase64 = canvas.toDataURL('image/png');
        URL.revokeObjectURL(url);
        resolve(pngBase64);
      };
      
      img.onerror = (err) => {
        URL.revokeObjectURL(url);
        console.error('Error loading logo:', err);
        resolve(null);
      };
      
      img.src = url;
    });
  } catch (error) {
    console.error('Error converting logo to PNG:', error);
    return null;
  }
};

/**
 * Generate Transaction Upload Report PDF
 * @param {string} userId - Firebase user UID
 * @param {Array} transactions - Array of transaction objects
 * @param {Object} balanceDeltas - Object with account names as keys and delta amounts as values
 * @param {string} logoBase64 - Optional base64 encoded logo image (auto-loaded if not provided)
 * @returns {Promise<Object>} - Returns { filename, downloadURL, storagePath }
 */
export const generateTransactionReport = async (userId, transactions, balanceDeltas = {}, logoBase64 = null) => {
  try {
    // Auto-load logo if not provided
    if (!logoBase64) {
      logoBase64 = await loadLogoBase64();
    }
    
    // 1. Generate PDF
    const pdfBlob = await createTransactionReportPDF(transactions, balanceDeltas, logoBase64);
    
    // 2. Upload to Firebase Storage and Firestore
    const reportInfo = await uploadReportToFirebase(userId, pdfBlob, transactions);
    
    return reportInfo;
  } catch (error) {
    console.error('Error generating transaction report:', error);
    throw error;
  }
};

/**
 * Create PDF document
 */
const createTransactionReportPDF = async (transactions, balanceDeltas, logoBase64) => {
  const doc = new jsPDF('landscape'); // Landscape orientation
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  // Add logo if provided
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, 'PNG', 14, 10, 30, 30);
    } catch (error) {
      console.warn('Could not add logo to PDF:', error);
    }
  }

  // Header - Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  const titleY = logoBase64 ? 25 : 20;
  doc.text('Transaction Upload Report', pageWidth / 2, titleY, { align: 'center' });
  
  // Header - Info
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const uploadDate = new Date().toLocaleDateString('en-GB');
  const uploadTime = new Date().toLocaleTimeString('en-GB');
  const infoY = logoBase64 ? 45 : 35;
  
  doc.text(`Upload Date: ${uploadDate}`, 14, infoY);
  doc.text(`Upload Time: ${uploadTime}`, 14, infoY + 5);
  doc.text(`Total Transactions: ${transactions.length}`, 14, infoY + 10);
  doc.text(`Accounts Affected: ${Object.keys(balanceDeltas).length}`, 14, infoY + 15);

  // Horizontal line
  doc.setLineWidth(0.5);
  doc.line(14, infoY + 20, pageWidth - 14, infoY + 20);

  // Group transactions by account
  const transactionsByAccount = {};
  transactions.forEach(txn => {
    if (!transactionsByAccount[txn.account]) {
      transactionsByAccount[txn.account] = [];
    }
    transactionsByAccount[txn.account].push(txn);
  });

  let currentY = infoY + 30;

  // Create a table for each account
  for (const [accountName, accountTransactions] of Object.entries(transactionsByAccount)) {
    // Account header
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Account: ${accountName}`, 14, currentY);
    currentY += 5;

    // Prepare table data for this account (removed Paid, Refund columns)
    const tableData = accountTransactions.map((txn, index) => [
      index + 1,
      txn.type,
      txn.name,
      `£${parseFloat(txn.amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      txn.category,
      txn.date,
      txn.period,
      extractUserNotes(txn.notes),
      txn.status || 'Validated & Uploaded'
    ]);

    // Add table for this account
    autoTable(doc, {
      startY: currentY,
      head: [['#', 'Type', 'Name', 'Amount', 'Category', 'Date', 'Period', 'Notes', 'Status']],
      body: tableData,
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [38, 166, 154],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },    // #
        1: { cellWidth: 22 },                      // Type
        2: { cellWidth: 38 },                      // Name
        3: { cellWidth: 28, halign: 'center' },    // Amount
        4: { cellWidth: 30 },                      // Category
        5: { cellWidth: 24 },                      // Date
        6: { cellWidth: 24 },                      // Period
        7: { cellWidth: 35 },                      // Notes
        8: { cellWidth: 35, halign: 'center' }     // Status
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { left: 14, right: 14 },
      tableWidth: 'auto'
    });

    currentY = doc.lastAutoTable.finalY + 5;

    // Add balance change for this account
    if (balanceDeltas[accountName] !== undefined) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      const delta = balanceDeltas[accountName];
      const deltaText = delta >= 0 
        ? `+£${delta.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
        : `-£${Math.abs(delta).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const deltaColor = delta >= 0 ? [38, 166, 154] : [255, 82, 82];
      doc.setTextColor(deltaColor[0], deltaColor[1], deltaColor[2]);
      doc.text(`Balance Change: ${deltaText}`, 20, currentY);
      doc.setTextColor(0, 0, 0); // Reset color
      currentY += 10;
    }

    // Check if we need a new page
    if (currentY > pageHeight - 40) {
      doc.addPage();
      currentY = 20;
    }
  }

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.text(
      `Generated by WebApp Bulk Transaction Entry - ${new Date().toLocaleString('en-GB')} - Page ${i} of ${pageCount}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
  }

  // Generate PDF as blob
  return doc.output('blob');
};

/**
 * Extract user notes by removing the prefix
 */
const extractUserNotes = (notes) => {
  if (!notes) return '';
  
  // Remove the "YYYY-MM - WebAPP upload - " prefix
  const match = notes.match(/^\d{4}-\d{2} - WebAPP upload - (.*)$/);
  if (match) {
    return match[1];
  }
  
  return notes;
};

/**
 * Generate filename with random characters
 */
const generateFilename = () => {
  const today = new Date();
  const dateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD
  const randomChars = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `Transactions_Webapp_${dateStr}_${randomChars}.pdf`;
};

/**
 * Get month key from today's date
 */
const getMonthKey = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * Upload report to Firebase Storage and save metadata to Firestore
 * NEW STRUCTURE: users/{uid}/cabinet/files/reports/{monthKey}
 * The monthKey is the document, and reports are added as fields within it
 */
const uploadReportToFirebase = async (userId, pdfBlob, transactions) => {
  try {
    const filename = generateFilename();
    const monthKey = getMonthKey();
    const storagePath = `users/${userId}/cabinet/reports/${monthKey}/${filename}`;

    // Upload to Firebase Storage
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, pdfBlob);
    const downloadURL = await getDownloadURL(storageRef);

    // Save metadata to Firestore: users/{uid}/cabinet/files/reports/{monthKey}
    // The monthKey is the document ID, and we add the report as a field
    const fileKey = filename.replace(/\./g, '_'); // Make filename Firestore-safe
    
    const monthDocRef = doc(db, 'users', userId, 'cabinet', 'files', 'reports', monthKey);
    
    await setDoc(monthDocRef, {
      [fileKey]: {
        fileName: filename,
        storagePath: storagePath,
        uploadedAt: new Date().toISOString(),
        transactionCount: transactions.length,
        uploadDate: new Date().toISOString().split('T')[0] // YYYY-MM-DD
      }
    }, { merge: true }); // merge: true to add this report alongside existing reports for this month

    console.log('✅ Report uploaded successfully:', filename);

    return {
      filename,
      downloadURL,
      storagePath,
      monthKey
    };
  } catch (error) {
    console.error('Error uploading report to Firebase:', error);
    throw error;
  }
};

/**
 * Helper function to convert SVG to base64 (for logo)
 * You can call this separately to prepare your logo
 */
export const svgToBase64 = (svgString) => {
  return `data:image/svg+xml;base64,${btoa(svgString)}`;
};